/**
 * Tauri desktop integration.
 *
 * The desktop build ships the SPA inside a Tauri (Rust + system webview)
 * shell that talks to the hosted Worker API at https://api.pollenate.dev.
 * Auth works exactly like the web app — Bearer tokens in the zustand store —
 * so most of the app is untouched. The desktop-specific bits are:
 *
 *  - hash routing (Tauri's asset protocol has no SPA fallback for deep paths)
 *  - OAuth via the system browser + `pollenate://` deep-link callback
 *    (Google blocks sign-in inside embedded webviews)
 *  - the auto-updater (signed artifacts verified against the public key in
 *    src-tauri/tauri.conf.json)
 *
 * On the web build every export here is inert (`isDesktop === false`), so
 * the same source powers both targets.
 */

/**
 * True when running inside the Tauri shell. `VITE_DESKTOP` is baked in at
 * build time (so routing decisions are stable before the webview injects
 * its globals); `__TAURI_INTERNALS__` is the runtime fallback.
 */
export const isDesktop: boolean =
  import.meta.env.VITE_DESKTOP === 'true' ||
  (typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window);

/**
 * Register the deep-link handler that receives
 * `pollenate://auth/callback?code=…` after the system-browser OAuth flow
 * completes. Invokes `onAuthCode` with the one-time code (the same code the
 * web flow receives at /auth/callback) so the app can redeem it via
 * POST /auth/oauth/exchange. Safe no-op on web.
 */
export async function initDesktopDeepLinks(
  onAuthCode: (code: string) => void,
): Promise<void> {
  if (!isDesktop) return;
  try {
    const { onOpenUrl } = await import('@tauri-apps/plugin-deep-link');
    await onOpenUrl((urls) => {
      for (const raw of urls) {
        try {
          const parsed = new URL(raw);
          if (parsed.protocol !== 'pollenate:') continue;
          if (parsed.host !== 'auth') continue;
          const code = parsed.searchParams.get('code');
          if (code) onAuthCode(code);
        } catch {
          /* ignore malformed deep links */
        }
      }
    });
  } catch {
    /* deep-link plugin unavailable — nothing to wire up */
  }
}

/**
 * Open an OAuth start URL in the user's real browser (Google blocks
 * embedded-webview sign-in). The `client=desktop` param on the URL tells the
 * worker callback to bounce the one-time code back through the
 * `pollenate://auth/callback` deep link instead of the web origin.
 */
export async function openInSystemBrowser(url: string): Promise<void> {
  try {
    const { openUrl } = await import('@tauri-apps/plugin-opener');
    await openUrl(url);
  } catch {
    // Last-ditch fallback if the plugin isn't available.
    window.open(url, '_blank');
  }
}

/**
 * Check the configured updater endpoint for a newer signed build and, if the
 * user agrees, download + install it and relaunch. The artifacts are verified
 * against the public key baked into `tauri.conf.json`, so only updates signed
 * with our private key are accepted. Safe no-op on web.
 *
 * `interactive` controls feedback when there's nothing to do: the silent
 * launch-time check (default) returns quietly, while a user-triggered
 * "Check for updates" passes `interactive: true` so the caller can confirm
 * the app is already current or surface an error.
 */
export type DesktopUpdateResult =
  | { status: 'unsupported' }
  | { status: 'up-to-date'; version: string }
  | { status: 'declined'; version: string }
  | { status: 'installing'; version: string }
  | { status: 'error'; message: string };

/**
 * Non-interactive update check used by the in-app update badge: reports
 * whether a newer signed desktop build is available without prompting the
 * user. Safe no-op on web.
 */
export async function getDesktopUpdateInfo(): Promise<{
  available: boolean;
  version?: string;
  notes?: string;
}> {
  if (!isDesktop) return { available: false };
  try {
    const { check } = await import('@tauri-apps/plugin-updater');
    const update = await check();
    if (!update) return { available: false };
    return { available: true, version: update.version, notes: update.body ?? undefined };
  } catch {
    return { available: false };
  }
}

export async function checkForDesktopUpdate(
  opts: { interactive?: boolean } = {},
): Promise<DesktopUpdateResult> {
  if (!isDesktop) return { status: 'unsupported' };
  try {
    const { check } = await import('@tauri-apps/plugin-updater');
    const update = await check();
    if (!update) {
      const version = await getDesktopVersion();
      return { status: 'up-to-date', version: version ?? '' };
    }

    // Use the Tauri dialog plugin's native `ask` rather than `window.confirm`:
    // wry's webview doesn't implement JS confirm panels, so `window.confirm`
    // returns `false` immediately and silently declines every update.
    const { ask } = await import('@tauri-apps/plugin-dialog');
    const proceed = await ask(
      `${update.body ?? 'A new version is ready to install.'}\n\n` +
        'Download and install it now? The app will restart when finished.',
      {
        title: `Pollenate ${update.version} is available`,
        kind: 'info',
        okLabel: 'Update now',
        cancelLabel: 'Later',
      },
    );
    if (!proceed) return { status: 'declined', version: update.version };

    await update.downloadAndInstall();

    const { relaunch } = await import('@tauri-apps/plugin-process');
    await relaunch();
    return { status: 'installing', version: update.version };
  } catch (err) {
    // Silent launch check: fail quietly and retry next launch. An explicit
    // user check returns the error so the UI can tell them it didn't work.
    if (!opts.interactive) return { status: 'error', message: 'updater unavailable' };
    return { status: 'error', message: (err as Error).message ?? String(err) };
  }
}

/**
 * The running desktop app's version (from `tauri.conf.json`, surfaced via
 * the Tauri app API). `null` on web or if the API is unavailable.
 */
export async function getDesktopVersion(): Promise<string | null> {
  if (!isDesktop) return null;
  try {
    const { getVersion } = await import('@tauri-apps/api/app');
    return await getVersion();
  } catch {
    return null;
  }
}
